import React, { Component } from "react";
import { Popover, Select, Spin } from "antd";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import debounce from "lodash/debounce";
import strings from "../../../../strings";
import { searchCustomersAsync } from "../../../../redux/actions/customerActions";

const Option = Select.Option;

class SelectCustomer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      searchResults: [],
      searching: false,
      hasSearched: false,
      renderKey: 0
    };
    
    this.textInputRef = React.createRef();
    
    // Bind debounced search function
    this.debouncedSearch = debounce(this.performSearch, 300);
  }

  componentDidMount() {
    // Load initial customers on mount (small set)
    this.loadInitialCustomers();
  }

  componentWillUnmount() {
    const { cleanupSelectCustomerDropdown } = this.props;
    cleanupSelectCustomerDropdown();
    this.debouncedSearch.cancel();
  }

  loadInitialCustomers = async () => {
    const { searchCustomersAsync } = this.props;
    try {
      const results = await searchCustomersAsync('', 20);
      this.setState({ searchResults: results || [] });
    } catch (error) {
      console.error('Error loading initial customers:', error);
    }
  }

  performSearch = async (searchTerm) => {
    const { searchCustomersAsync } = this.props;
    
    if (!searchTerm || searchTerm.trim() === '') {
      // When search is cleared, reload initial customers
      this.loadInitialCustomers();
      this.setState({
        hasSearched: false,
        searching: false
      });
      return;
    }

    this.setState({ searching: true });
    try {
      const results = await searchCustomersAsync(searchTerm, 20);
      this.setState({
        searchResults: results || [],
        hasSearched: true
      });
    } catch (error) {
      console.error('Error searching customers:', error);
      this.setState({ searchResults: [] });
    } finally {
      this.setState({ searching: false });
    }
  }

  focus = () => {
    if (this.textInputRef.current) {
      this.textInputRef.current.focus();
    }
  }

  // Get the actual value to display in the Select component
  getSelectValue = () => {
    const { customerValue, customerName, type, instoreOrders } = this.props;
    
    if (instoreOrders) {
      return customerValue;
    }
    if (type === "edit") {
      return customerName;
    }
    
    // Handle both array and single value cases
    let valueToReturn;
    if (Array.isArray(customerValue)) {
      valueToReturn = customerValue.length > 0 ? customerValue[0] : undefined;
    } else {
      valueToReturn = customerValue;
    }
    
    return valueToReturn;
  }

  getCustomField = (customer, field = "cars") => {
    if (customer.customer_custom_fields) {
      const fieldObj = customer.customer_custom_fields.find(
        one => one.name.toLowerCase() === field
      );
      if (fieldObj) return fieldObj.value;
      else return "";
    } else return "";
  }

  // Handle search input
  // Searches customers by: name (first/last), phone number, email, or loyalty ID
  // The search is debounced (300ms) to prevent excessive API calls
  handleSearch = (value) => {
    if (value && value !== 'add') {
      this.setState({ hasSearched: true });
      this.debouncedSearch(value);
    }
  }

  render() {
    const {
      customers,
      customerValue,
      onCustomerSearch,
      customerName,
      isDisabled,
      handleBlurFactor
    } = this.props;
    
    const { searchResults, searching, hasSearched, renderKey } = this.state;

    // Get customers to display - prefer search results if searched, otherwise use passed customers
    let displayCustomers = hasSearched ? searchResults : (customers || []);
    
    // Ensure selected customer is in the display list
    if (customerValue && displayCustomers) {
      const isSelectedInList = displayCustomers.find(c => c.user_id === customerValue);
      if (!isSelectedInList && customers) {
        // Find the selected customer in the original customers list
        const selectedCustomer = customers.find(c => c.user_id === customerValue);
        if (selectedCustomer) {
          // Add it to the display list
          displayCustomers = [selectedCustomer, ...displayCustomers];
        }
      }
    }

    return (
      <div
        style={{
          padding: "5px",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Select
          allowClear
          showSearch
          key={`select-${renderKey}`}
          value={this.getSelectValue()}
          placeholder={strings.searchCustomerNameOrNumber}
          optionFilterProp="children"
          onChange={onCustomerSearch}
          onSearch={this.handleSearch}
          size="large"
          style={{ width: "80%" }}
          filterOption={false} // Disable client-side filtering since we're doing server-side search
          notFoundContent={searching ? <Spin size="small" /> : null}
          onDeselect={e => console.log(e)}
          disabled={isDisabled === undefined ? false : isDisabled}
          ref={this.textInputRef}
          onMouseEnter={() => handleBlurFactor && handleBlurFactor(false)}
        >
          <Option key="add">{strings.addNewCustomer}</Option>
          {/* Add temporary option for newly added customer if not in list yet */}
          {customerValue && customerName && displayCustomers && 
           !displayCustomers.find(c => c.user_id === customerValue) && (
            <Option
              key={`temp-${customerValue}`}
              value={customerValue}
            >
              {customerName}
            </Option>
          )}
          {displayCustomers &&
            displayCustomers.map(customer => (
              <Option
                key={customer.user_id}
                value={customer.user_id}
                id={customer.loyalty_id}
              >
                {`${customer.first_name ? customer.first_name : "N/A"} ${
                  customer.last_name ? customer.last_name : ""
                }${
                  customer.phone_number ? " : " + customer.phone_number : ""
                }${
                  customer.loyalty_id ? " (ID: " + customer.loyalty_id + ")" : ""
                } ${this.getCustomField(customer, "car type")}  ${this.getCustomField(
                  customer,
                  "plate number"
                )}
                `}
              </Option>
            ))}
        </Select>

        <div
          className="scanner_icon"
          style={{ display: "flex", justifyContent: "right" }}
        >
          <Popover
            content={
              <span style={{ width: "150px" }}>
                {strings.clickTheSearchCustomerBox}
              </span>
            }
            placement="topRight"
          >
            <img
              src="/assets/img/scan_card.png"
              alt="Scan Card"
              style={{ width: "50px" }}
              onClick={this.focus}
            />
          </Popover>
        </div>
      </div>
    );
  }
}

SelectCustomer.propTypes = {
  customers: PropTypes.arrayOf(PropTypes.object).isRequired,
  customerValue: PropTypes.oneOfType([PropTypes.array, PropTypes.number, PropTypes.string]),
  user: PropTypes.object.isRequired,
  onCustomerSearch: PropTypes.func.isRequired,
  searchCustomersAsync: PropTypes.func.isRequired,
  cleanupSelectCustomerDropdown: PropTypes.func.isRequired,
};

const mapDispatchToProps = {
  searchCustomersAsync,
};

export default connect(null, mapDispatchToProps)(SelectCustomer);
